<script setup lang="ts">
// Shared comment composer: the Quill editor plus a send button that only
// appears once something has actually been typed, mirroring the inbox message
// box. Owners keep the posting logic — this emits what was written and lets
// the parent decide where it goes.
import { QuillEditor } from "@vueup/vue-quill";
import { computed, ref } from "vue";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import "quill-mention";
import { useConfigStore } from "~/stores/configStore";
import { SimpleUser } from "~/types/user";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    /** Shown on hover when disabled, e.g. a missing permission. */
    disabledTitle?: string;
    placeholder?: string;
    /**
     * Set false where the box is part of a bigger form that submits the comment
     * itself — the parent then reads the content through `getContent()` instead
     * of waiting for a `submit` of its own.
     */
    showSend?: boolean;
  }>(),
  {
    disabled: false,
    disabledTitle: "",
    placeholder: "Type @ to mention and notify someone.",
    showSend: true,
  },
);

const emit = defineEmits<{
  (e: "submit", payload: { body: string; mentionedIds: number[] }): void;
}>();

const configStore = useConfigStore();

const editor = ref();
const text = ref();

const atValues = ref([] as SimpleUser[]);

const setAtValues = () => {
  atValues.value = (configStore.globalConfig.agents ?? []).map(
    ({ name, id, email }: any) => {
      const fullText = `${name} | ${email}`;
      return {
        id,
        value: fullText.length > 28 ? fullText.slice(0, 25) + "..." : fullText,
      };
    },
  );
};

onMounted(setAtValues);

const options = {
  modules: {
    toolbar: ["bold", "italic", "underline", "strike", "link", { color: [] }],
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      mentionDenotationChars: ["@"],
      source(searchTerm: string, renderList: any, mentionChar: string) {
        const values = mentionChar === "@" ? atValues.value : [];

        if (!searchTerm.length) {
          renderList(values, searchTerm);
          return;
        }

        renderList(
          values.filter((v) =>
            v.value.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
          searchTerm,
        );
      },
    },
  },
};

// Quill's "empty" document is a single newline, so an emptiness test has to
// look at the ops rather than at truthiness. Embeds (a mention on its own)
// count as content even though they contribute no text.
const hasContent = computed(() => {
  const ops = text.value?.ops;

  if (!Array.isArray(ops)) return false;

  return ops.some((op: any) =>
    typeof op?.insert === "string" ? op.insert.trim() !== "" : !!op?.insert,
  );
});

const mentionedIds = computed<number[]>(() => {
  const ops = text.value?.ops;

  if (!Array.isArray(ops)) return [];

  return [
    ...new Set(
      ops
        .map((op: any) => op?.insert?.mention?.id)
        .filter((id: unknown) => id !== undefined)
        .map(Number),
    ),
  ];
});

const reset = () => {
  editor.value?.setHTML?.("");
  text.value = undefined;
};

const submit = () => {
  if (props.disabled || !hasContent.value) return;

  emit("submit", {
    body: editor.value?.getHTML?.() ?? "",
    mentionedIds: mentionedIds.value,
  });
};

// What the box currently holds, or null when nothing has been typed. For forms
// that carry the comment in their own payload rather than posting it here.
const getContent = () =>
  hasContent.value
    ? {
        body: editor.value?.getHTML?.() ?? "",
        mentionedIds: mentionedIds.value,
      }
    : null;

// Parents clear the box only once the post has actually succeeded, so nothing
// typed is lost when the request fails.
defineExpose({ reset, getContent, focus: () => editor.value?.focus?.() });
</script>

<template>
  <div class="comment-composer relative">
    <div class="leadCommentToolbar w-full p-1">
      <quill-editor
        ref="editor"
        v-model:content="text"
        theme="snow"
        :options="options"
        :placeholder="props.placeholder"
        class="leadComment"
      />
    </div>

    <!-- Mirrors the inbox composer: nothing at rest, and a send button sitting
         inside the input as soon as there is something to send. -->
    <div
      v-if="hasContent && props.showSend"
      class="absolute bottom-3 right-3 z-10"
    >
      <NavButton
        class="composer-send-btn h-9 w-9 shrink-0 justify-center rounded-full bg-light-purple-nav shadow-md shadow-slate-900/10"
        :disabled="props.disabled"
        :title="props.disabled ? props.disabledTitle : 'Send'"
        @click="submit"
      >
        <svg
          class="w-4"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </NavButton>
    </div>
  </div>
</template>

<style scoped>
/* The send button is overlaid in the bottom-right of the input, so the editor
   needs room underneath or long text runs behind it. */
.comment-composer :deep(.ql-editor) {
  padding-bottom: 2.75rem;
}
</style>
